import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { format, isPast } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  Video,
  Users,
  Target,
  Sparkles,
  FileText,
  Mail,
  Trash2,
  Play,
} from "lucide-react";
import Card from "../components/Card";
import Button from "../components/Button";
import LoadingSkeleton from "../components/LoadingSkeleton";
import { apiRequest } from "../lib/queryClient";
import type { Call } from "../types";

export default function CallDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<Call | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingBrief, setIsGeneratingBrief] = useState(false);
  const [isGeneratingFollowUp, setIsGeneratingFollowUp] = useState(false);

  useEffect(() => {
    if (id) {
      loadCall(id);
    }
  }, [id]);

  const loadCall = async (callId: string) => {
    try {
      const response = await fetch(`/api/calls/${callId}`);
      if (response.ok) {
        const data = await response.json();
        setCall(data);
      }
    } catch (error) {
      console.error("Error loading call:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateCall = async (updates: Partial<Call>) => {
    if (!call) return null;
    try {
      const response = await apiRequest("PUT", `/api/calls/${call.id}`, updates);
      if (response.ok) {
        const updated = await response.json();
        setCall(updated);
        return updated;
      }
    } catch (error) {
      console.error("Error updating call:", error);
    }
    return null;
  };

  const handleGenerateBrief = async () => {
    if (!call) return;
    setIsGeneratingBrief(true);
    try {
      const response = await apiRequest("POST", "/api/ai/generate-brief", {
        clientName: call.clientName,
        goals: call.goals,
        participants: (call.participants || []).map((p) => ({
          name: p.name,
          title: p.title,
        })),
        notes: call.prepPhase?.criticalInsights?.map((i) => i.insight).join("; "),
      });
      const data = await response.json();
      await updateCall({ aiBrief: data.brief });
    } catch (error) {
      console.error("Error generating brief:", error);
    } finally {
      setIsGeneratingBrief(false);
    }
  };

  const handleGenerateFollowUp = async () => {
    if (!call) return;
    setIsGeneratingFollowUp(true);
    try {
      const response = await apiRequest("POST", "/api/ai/generate-followup", {
        clientName: call.clientName,
        transcription: call.transcription,
        goals: call.goals,
      });
      const data = await response.json();
      await updateCall({
        followUpEmail: data.email,
        followUpStrategy: data.strategy,
        status: "completed",
      });
    } catch (error) {
      console.error("Error generating follow-up:", error);
    } finally {
      setIsGeneratingFollowUp(false);
    }
  };

  const handleDelete = async () => {
    if (!call || !confirm("Are you sure you want to delete this call?")) return;
    try {
      const response = await apiRequest("DELETE", `/api/calls/${call.id}`);
      if (response.ok) {
        navigate("/calls");
      }
    } catch (error) {
      console.error("Error deleting call:", error);
    }
  };

  if (isLoading) {
    return <LoadingSkeleton count={3} />;
  }

  if (!call) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Call not found</p>
        <Button variant="ghost" onClick={() => navigate("/calls")} className="mt-4">
          Back to Calls
        </Button>
      </div>
    );
  }

  const participants = call.participants || [];
  const isUpcoming = !isPast(new Date(call.scheduledAt)) && call.status !== "completed";

  return (
    <div>
      <button
        onClick={() => navigate("/calls")}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Calls
      </button>

      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{call.clientName}</h1>
          {call.companyName && (
            <p className="text-gray-500">{call.companyName}</p>
          )}
        </div>
        <div className="flex gap-2">
          {isUpcoming && (
            <Button onClick={() => navigate(`/calls/${call.id}/prepare`)}>
              <Play className="w-4 h-4" />
              Prepare
            </Button>
          )}
          <Button variant="outline" onClick={handleDelete}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <h3 className="font-semibold text-gray-900 mb-3">Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-gray-400" />
              {format(new Date(call.scheduledAt), "EEEE, MMMM d, yyyy")}
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-gray-400" />
              {format(new Date(call.scheduledAt), "h:mm a")}
            </div>
            <div className="flex items-center gap-2 text-gray-600">
              {call.isVirtual ? (
                <>
                  <Video className="w-4 h-4 text-gray-400" />
                  Virtual Meeting
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 text-gray-400" />
                  {call.location || "Location not set"}
                </>
              )}
            </div>
          </div>
        </Card>

        <Card>
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <Users className="w-4 h-4 text-gray-400" />
            Participants ({participants.length})
          </h3>
          {participants.length === 0 ? (
            <p className="text-sm text-gray-500">No participants added</p>
          ) : (
            <div className="space-y-2">
              {participants.map((p) => (
                <div key={p.id} className="text-sm">
                  <p className="font-medium text-gray-900">{p.name}</p>
                  {p.title && <p className="text-gray-500">{p.title}</p>}
                </div>
              ))}
            </div>
          )}
        </Card>

        {call.goals && (
          <Card className="md:col-span-2">
            <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <Target className="w-4 h-4 text-gray-400" />
              Goals
            </h3>
            <p className="text-sm text-gray-600 whitespace-pre-wrap">{call.goals}</p>
          </Card>
        )}

        <Card className="md:col-span-2">
          <div className="flex justify-between items-start mb-3">
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              AI Brief
            </h3>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleGenerateBrief}
              loading={isGeneratingBrief}
            >
              {call.aiBrief ? "Regenerate" : "Generate"}
            </Button>
          </div>
          {call.aiBrief ? (
            <div className="prose prose-sm max-w-none text-gray-600">
              <pre className="whitespace-pre-wrap font-sans text-sm">{call.aiBrief}</pre>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              Generate an AI-powered brief with talking points and questions
            </p>
          )}
        </Card>

        {(call.transcription || !isUpcoming) && (
          <>
            <Card className="md:col-span-2">
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-400" />
                Transcription
              </h3>
              {call.transcription ? (
                <p className="text-sm text-gray-600 whitespace-pre-wrap">
                  {call.transcription}
                </p>
              ) : (
                <p className="text-sm text-gray-500">No transcription available</p>
              )}
            </Card>

            <Card className="md:col-span-2">
              <div className="flex justify-between items-start mb-3">
                <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-primary" />
                  Follow-Up Email
                </h3>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={handleGenerateFollowUp}
                  loading={isGeneratingFollowUp}
                  disabled={!call.transcription}
                >
                  {call.followUpEmail ? "Regenerate" : "Generate"}
                </Button>
              </div>
              {call.followUpEmail ? (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-600">
                  {call.followUpEmail}
                </pre>
              ) : (
                <p className="text-sm text-gray-500">
                  {call.transcription
                    ? "Generate a follow-up email based on the call"
                    : "Add transcription first to generate follow-up email"}
                </p>
              )}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
